import React, { useEffect, useState } from 'react';
import { Loader2, Trash2, Mail, Phone, IndianRupee } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { api, formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    api
      .get('/admin/bookings')
      .then((r) => setBookings(r.data))
      .catch(() => toast.error('Failed to load bookings'))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    setDeleting(id);
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success('Booking deleted');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-bookings-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Bookings</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{bookings.length} booking(s) received</p>
      </div>

      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-red-600" /></div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">No bookings yet</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Booking ID</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Customer</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Item</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Total</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Date</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-gray-300 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {bookings.map((b) => (
                  <tr key={b.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-700 dark:text-gray-300">{b.id}</td>
                    <td className="px-4 py-3"><Badge className="bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 capitalize">{b.type}</Badge></td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                      <p className="font-semibold">{b.name}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1"><Mail className="h-3 w-3" />{b.email}</p>
                      <p className="text-xs text-gray-500 flex items-center gap-1"><Phone className="h-3 w-3" />{b.phone}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-[200px] truncate">
                      {b.item_snapshot?.name || b.item_snapshot?.airline || b.item_snapshot?.trainName || b.item_snapshot?.operator || b.item_snapshot?.model || b.item_id}
                    </td>
                    <td className="px-4 py-3 font-semibold text-gray-900 dark:text-white flex items-center"><IndianRupee className="h-3.5 w-3.5" />{Number(b.total_amount).toLocaleString()}</td>
                    <td className="px-4 py-3"><Badge className="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300">{b.status}</Badge></td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDelete(b.id)}
                        disabled={deleting === b.id}
                        data-testid={`delete-booking-${b.id}`}
                        className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/30 rounded text-red-600 dark:text-red-400 disabled:opacity-50"
                      >
                        {deleting === b.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminBookings;
